import { useState, useLayoutEffect, useRef, useMemo } from 'react';

/**
 * Coordinates mount/unmount with enter/exit animation.
 * Enter: initial `visible: false` paints closed, then rAF → `visible: true`.
 * Exit: `visible: false` immediately, unmount after `duration` ms.
 *
 * @param {boolean} isOpen
 * @param {number} duration - exit animation duration (ms) before unmount
 * @param {boolean} [skipEnter=false] - appear instantly with no enter animation (seamless modal handoff)
 * @returns {{ mounted: boolean, visible: boolean }}
 */
export function useOverlayTransition(isOpen, duration, skipEnter = false) {
  const [mounted, setMounted] = useState(isOpen);
  const [visible, setVisible] = useState(isOpen && skipEnter);
  const exitTimerRef = useRef(null);
  const enterRafRef = useRef(null);
  // Read skipEnter through a ref so flipping it false→true (the provider clearing
  // seamlessEnter) does NOT re-run the effect and re-trigger an enter animation.
  const skipEnterRef = useRef(skipEnter);
  skipEnterRef.current = skipEnter;

  const preferCoarsePointer = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }, []);

  useLayoutEffect(() => {
    if (enterRafRef.current != null) {
      cancelAnimationFrame(enterRafRef.current);
      enterRafRef.current = null;
    }

    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    if (isOpen) {
      setMounted(true);

      // Seamless mount: appear instantly with no enter transition (handed-off
      // modal). Only short-circuits the very first reveal of this open-cycle;
      // later re-opens (skipEnter false) animate normally.
      if (skipEnterRef.current) {
        setVisible(true);
        return undefined;
      }

      // Pin closed state before rAF reveal so the slide/fade enter transition runs.
      setVisible(false);

      let cancelled = false;
      const reveal = () => {
        enterRafRef.current = null;
        if (!cancelled) setVisible(true);
      };

      // iOS Safari often skips the first transition if visible flips on the
      // very next frame — one extra rAF on touch keeps enter motion reliable.
      if (preferCoarsePointer) {
        enterRafRef.current = requestAnimationFrame(() => {
          enterRafRef.current = requestAnimationFrame(reveal);
        });
      } else {
        enterRafRef.current = requestAnimationFrame(reveal);
      }

      return () => {
        cancelled = true;
        if (enterRafRef.current != null) {
          cancelAnimationFrame(enterRafRef.current);
          enterRafRef.current = null;
        }
      };
    }

    setVisible(false);
    exitTimerRef.current = setTimeout(() => {
      setMounted(false);
      exitTimerRef.current = null;
    }, duration);

    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [isOpen, duration, preferCoarsePointer]);

  return { mounted, visible };
}
